import * as React from 'react'
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn
} from '@axilon-apps/libs-ui-shadcn'

import { calculateStickyStyles } from './table-utils'

export interface ColumnDef<TRowModel> {
  align?: 'left' | 'right' | 'center'
  field?: keyof TRowModel
  formatter?: (row: TRowModel, index: number) => React.ReactNode
  hideName?: boolean
  name: string
  width?: number | string
  breakWord?: boolean
  onClick?: (row: TRowModel, index: number) => void
  excludeFromRowClick?: boolean
  sticky?: 'left' | 'right'
  stickyBorder?: boolean
}

type RowId = number | string

export interface Props<TRowModel> {
  columns: ColumnDef<TRowModel>[]
  className?: string
  hoverRow?: boolean
  stripe?: 'even' | 'odd'
  onDeselectAll?: (event: React.ChangeEvent) => void
  onDeselectOne?: (event: React.ChangeEvent, row: TRowModel) => void
  onSelectAll?: (event: React.ChangeEvent) => void
  onSelectOne?: (event: React.ChangeEvent, row: TRowModel) => void
  onRowClick?: (row: TRowModel, index: number) => void
  rows: TRowModel[]
  selectable?: boolean
  selected?: Set<RowId>
  uniqueRowId?: (row: TRowModel) => RowId
}

const stickyOffsetStyle = (
  sticky: 'left' | 'right',
  offset: number,
  stickyBorder: boolean | undefined,
  zIndex: number
): React.CSSProperties => ({
  position: 'sticky',
  zIndex,
  ...(sticky === 'left' && {
    left: offset,
    ...(stickyBorder && { borderRight: '1px solid var(--border)' })
  }),
  ...(sticky === 'right' && {
    right: offset,
    ...(stickyBorder && { borderLeft: '1px solid var(--border)' })
  })
})

const DataTable = <TRowModel extends object & { id?: RowId | null }>({
  columns,
  className,
  hoverRow,
  stripe,
  onDeselectAll,
  onDeselectOne,
  onSelectOne,
  onSelectAll,
  onRowClick,
  rows,
  selectable,
  selected,
  uniqueRowId
}: Props<TRowModel>): React.JSX.Element => {
  const selectedSome = (selected?.size ?? 0) > 0 && (selected?.size ?? 0) < rows.length
  const selectedAll = rows.length > 0 && selected?.size === rows.length

  const stickyStyles = React.useMemo(
    () => calculateStickyStyles(columns, selectable ?? false),
    [columns, selectable]
  )

  return (
    <Table className={cn('[&_td]:px-5 [&_td]:py-3 [&_th]:px-5 [&_th]:py-3', className)}>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {selectable ? (
            <TableHead className="bg-card sticky left-0 z-[3] w-[60px] min-w-[60px]">
              <Checkbox
                checked={selectedAll ? true : selectedSome ? 'indeterminate' : false}
                onCheckedChange={(next) => {
                  const event = { target: { checked: next } } as unknown as React.ChangeEvent
                  if (selectedAll || next === false) {
                    onDeselectAll?.(event)
                  } else {
                    onSelectAll?.(event)
                  }
                }}
              />
            </TableHead>
          ) : null}
          {columns.map((column) => {
            const stickyOffset = column.sticky
              ? column.sticky === 'left'
                ? stickyStyles.leftOffsets[column.name]
                : stickyStyles.rightOffsets[column.name]
              : 0
            const stickyStyle = column.sticky
              ? stickyOffsetStyle(column.sticky, stickyOffset, column.stickyBorder, 2)
              : undefined

            return (
              <TableHead
                key={column.name}
                // Sticky header cells need an opaque background so other cells
                // can slide under them on horizontal scroll. Use bg-card so the
                // sticky cell matches the surrounding (transparent over Card)
                // header cells; bg-background is var(--bg), which is a slightly
                // different shade than the Card wrapper's var(--bg-elev).
                className={cn(column.sticky && 'bg-card')}
                style={{
                  width: column.width,
                  minWidth: column.width,
                  maxWidth: column.width,
                  ...(column.align && { textAlign: column.align }),
                  ...stickyStyle
                }}
              >
                {column.hideName ? null : column.name}
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index): React.JSX.Element => {
          const rowId = row.id ? row.id : uniqueRowId?.(row)
          const rowSelected = rowId ? (selected?.has(rowId) ?? false) : false
          const clickable = Boolean(onRowClick)

          // Pick the row's background explicitly per index so sticky cells
          // can use `bg-inherit` and pick up the same stripe colour. Tailwind's
          // even:/odd: variants only apply to the element they're declared on,
          // not its descendants, so the previous `even:bg-muted/40` on the
          // <TableRow> never reached the sticky <TableCell> — which always
          // rendered bg-background and broke the stripe in the action column.
          const isStripedRow =
            (stripe === 'even' && index % 2 === 1) || (stripe === 'odd' && index % 2 === 0)
          const rowBg = isStripedRow ? 'bg-muted/40' : 'bg-card'

          // Sticky cell backgrounds are split across "resting" and
          // "row-hovered" states. Pre-fix they were applied via inline
          // `style={{ backgroundColor }}` which beats CSS specificity and
          // froze sticky cells at their resting colour even when the
          // shadcn TableRow's `hover:bg-muted/50` fired on the rest of the
          // row — so the action column would stay light while the row
          // around it tinted on hover, which read as a broken hover state.
          // Now expressed as Tailwind classes with `group-hover:` variants
          // (the row carries the `group` class below), so the sticky cell
          // matches the hover wash exactly.
          //
          // Why color-mix-over-card and not plain `bg-muted/50`: the sticky
          // cell sits above scrolled content and needs to be FULLY OPAQUE
          // so the body cells beneath can't bleed through. The opaque
          // equivalent of `bg-muted/50` on a `bg-card` row is the 50:50
          // mix; for striped rows (already 40% muted), the on-hover
          // equivalent is approximately 70% muted (50% hover wash composed
          // over the existing 40% wash).
          //
          // `group-hover:` fires whenever the row is hovered, regardless of
          // whether the row itself paints a hover background. For tables
          // that opt OUT of row hover (`hoverRow={false}` and no row click
          // handler — see MachinesTable), we'd otherwise tint the sticky
          // cell on hover while the rest of the row stayed flat, which
          // reads as broken. Gate the `group-hover:` class on the same
          // hover-active condition the row uses.
          const isRowHoverable = Boolean(hoverRow || clickable)
          const stickyRestBg = isStripedRow
            ? 'bg-[color-mix(in_oklch,var(--muted)_40%,var(--card))]'
            : 'bg-card'
          const stickyHoverBg = !isRowHoverable
            ? ''
            : isStripedRow
              ? 'group-hover:bg-[color-mix(in_oklch,var(--muted)_70%,var(--card))]'
              : 'group-hover:bg-[color-mix(in_oklch,var(--muted)_50%,var(--card))]'
          // `transition-colors` syncs the sticky cell's bg fade with the
          // shadcn TableRow's own `transition-colors`. Without it, the
          // sticky cell snaps to its hover colour at t=0 while the row
          // fades over ~150ms — you can see a 'row first, then action
          // column' lag during the transition.
          const stickyBgClass = cn(stickyRestBg, stickyHoverBg, 'transition-colors')

          return (
            <TableRow
              key={rowId ?? index}
              className={cn(
                // `group` so sticky cells below can react to row hover via
                // `group-hover:` (see stickyBgClass).
                'group',
                rowBg,
                !isRowHoverable && 'hover:bg-transparent',
                clickable && 'cursor-pointer'
              )}
              onClick={clickable ? () => onRowClick?.(row, index) : undefined}
            >
              {selectable ? (
                <TableCell
                  onClick={(event) => event.stopPropagation()}
                  className={cn('sticky left-0 z-[1]', stickyBgClass)}
                >
                  <Checkbox
                    checked={rowId ? rowSelected : false}
                    onCheckedChange={(next) => {
                      const event = {
                        target: { checked: next }
                      } as unknown as React.ChangeEvent
                      if (rowSelected || next === false) {
                        onDeselectOne?.(event, row)
                      } else {
                        onSelectOne?.(event, row)
                      }
                    }}
                  />
                </TableCell>
              ) : null}

              {columns.map((column) => {
                const shouldBreak = column.breakWord ?? true
                const isClickableColumn = Boolean(column.onClick)
                const shouldPreventRowClick = column.onClick || column.excludeFromRowClick

                const stickyOffset = column.sticky
                  ? column.sticky === 'left'
                    ? stickyStyles.leftOffsets[column.name]
                    : stickyStyles.rightOffsets[column.name]
                  : 0
                const stickyStyle = column.sticky
                  ? stickyOffsetStyle(column.sticky, stickyOffset, column.stickyBorder, 1)
                  : undefined

                const handleClick = shouldPreventRowClick
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      column.onClick?.(row, index)
                    }
                  : undefined

                return (
                  <TableCell
                    key={column.name}
                    onClick={handleClick}
                    className={cn(
                      shouldBreak
                        ? 'break-words whitespace-normal'
                        : 'overflow-hidden text-ellipsis whitespace-nowrap',
                      isClickableColumn && 'hover:bg-muted/60 cursor-pointer hover:underline',
                      // Class-based opaque sticky bg with `group-hover:`
                      // hover variant — see comment near `stickyBgClass`
                      // definition above for why this is class-based rather
                      // than inline `style`.
                      column.sticky && stickyBgClass
                    )}
                    style={{
                      ...(column.align && { textAlign: column.align }),
                      ...stickyStyle
                    }}
                  >
                    {
                      (column.formatter
                        ? column.formatter(row, index)
                        : column.field
                          ? row[column.field]
                          : null) as React.ReactNode
                    }
                  </TableCell>
                )
              })}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default DataTable
